import React from 'react';
import { Form, Input, Button, Space, Typography, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * Repeatable {label, url} list for `AdvisoryFirm.paymentLinksJson` — the LA's own external
 * payment-collection links (UPI / Razorpay Payment Page / Stripe / etc.), independent of this
 * platform's own billing (Workstream B, out of scope here).
 *
 * Must be rendered inside an antd <Form> — uses Form.List under the field name "paymentLinks".
 * The parent form owns serialising the resulting array to/from `paymentLinksJson` on
 * load/submit (JSON.stringify / JSON.parse) since this component only edits the in-form array.
 *
 * Per the spec's 2026-09-28 amendment: this field is explicitly NEVER populated by the
 * AI-autofetch step — there is no "AI drafted" affordance anywhere in this component by design.
 */
const AdvisoryFirmPaymentLinksEditor = () => (
    <div>
        <Text style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Payment links
        </Text>
        <Alert
            style={{ marginTop: 6, marginBottom: 10 }}
            type="info"
            showIcon
            message="These are your own external payment-collection links - not PolicyLens billing"
            description="Paste links you already use to collect money from your clients (a UPI link, your Razorpay Payment Page, a Stripe link, etc.). This is separate from - and unrelated to - any future PolicyLens platform subscription billing. Entered manually here; never auto-filled by AI."
        />
        <Form.List name="paymentLinks">
            {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {fields.map(({ key, name, ...restField }) => (
                        <Space key={key} align="baseline" style={{ width: '100%', display: 'flex' }}>
                            <Form.Item
                                {...restField}
                                name={[name, 'label']}
                                rules={[{ required: true, message: 'Label required' }]}
                                style={{ marginBottom: 0, width: 160 }}
                            >
                                <Input placeholder="e.g. UPI" />
                            </Form.Item>
                            <Form.Item
                                {...restField}
                                name={[name, 'url']}
                                rules={[
                                    { required: true, message: 'URL required' },
                                    { type: 'url', message: 'Enter a valid URL', warningOnly: false },
                                ]}
                                style={{ marginBottom: 0, flex: 1 }}
                            >
                                <Input placeholder="https://razorpay.me/@yourfirm" />
                            </Form.Item>
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(name)}
                                aria-label="Remove payment link"
                            />
                        </Space>
                    ))}
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                        Add payment link
                    </Button>
                </Space>
            )}
        </Form.List>
    </div>
);

export default AdvisoryFirmPaymentLinksEditor;
